<script setup lang="ts">
import { PropType } from 'vue'
import { useBasketStore } from '~/stores/basket'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import DataLayer from '~/extensions/DataLayer'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import { transformProductToGtmProduct } from '~/utils/transformProductToGtmProduct'
import SubstitutionProduct from '~/models/Product/SubstitutionProduct'
import Product from '~/models/Product/Product'
import { transformSubstitutionProductToProduct } from '~/utils/transformSubstitutionProductToProduct'

const props = defineProps({
    item: {
        type: Object as PropType<SubstitutionProduct>,
        required: true,
    },
    isSingle: {
        type: Boolean,
        default: false,
    },
    listPosition: {
        type: Number,
        required: true,
    },
    substitutionType: {
        type: String as PropType<'manual' | 'automatic'>,
        required: true,
    },
})

const { item } = toRefs(props)
const product = ref<Product>(transformSubstitutionProductToProduct(item.value))
const itemListName =
    props.substitutionType === 'manual'
        ? GtmItemListNameEnum.ProductDetailSubstitutionManualNonAggressive
        : GtmItemListNameEnum.ProductDetailSubstitutionAutoNonAggressive

const redirectToUrl = async (event: Event): Promise<void> => {
    setTimeout(() => {
        // do long task in new thread

        const ecommerceEvent = {
            type: GtmEcommerceEventTypeEnum.ClickToProductDetail,
            products: [transformProductToGtmProduct(product.value)],
            targetListPosition: props.listPosition,
            itemListName,
        } as GtmEcommerceEvent

        DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)
    }, 0)

    await safeNavigateTo((event.currentTarget as HTMLAnchorElement).getAttribute('href'))
}

const authUser = computed(() => {
    const basketStore = useBasketStore()

    return basketStore.authUser
})
</script>

<template>
    <div class="recommended-product" :class="{ 'single-item': isSingle }">
        <div class="recommended-product__image">
            <ObjectsProductImage
                :product="product"
                :lazy-image="true"
                :small="true"
                :auth-user="authUser"
                @redirect-to-url="redirectToUrl"
            />
        </div>
        <div class="recommended-product__content">
            <ObjectsProductName :product="product" @redirect-to-url="redirectToUrl" />
            <div class="recommended-product__price-info">
                <ObjectsProductAvailability
                    :product="product"
                    :has-variants="false /*item.hasOwnProperty('variantsCount') && item.variantsCount > 0*/"
                />

                <ObjectsProductPrices :product="product" :price-css="'fs-20'" @redirect-to-url="redirectToUrl" />

                <ElementsButtonAddBasket
                    v-if="product.isAvailable && product.variantsCount === 0"
                    ref="addToBasketButton"
                    :add-basket-align="``"
                    :button-class="`btn btn-outline-primary btn-rounded-circle btn--lg py-1 w-100`"
                    :product="product"
                    :product-id="product.id"
                    :product-price="product.price"
                    :is-preorder="product.isPreorder"
                    :max-amount-per-order="product.attributes.maxAmountPerOrder"
                    :has-substitution-modal="product.substitutions?.isModal || false"
                    :item-list-name="itemListName"
                />

                <!--                <ObjectsProductVariantsLink-->
                <!--                    v-if="item.hasOwnProperty('variantsCount') && item.variantsCount > 0"-->
                <!--                    :product="item"-->
                <!--                    @redirect-to-url="redirectToUrl"-->
                <!--                />-->
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.recommended-product {
    display: flex;
    margin-bottom: 20px;

    &__price-info {
        @include screen-md {
            padding-right: 6px;
            width: 285px;
        }
    }

    @include screen-md {
        margin-bottom: 0;
        flex: 1 0 50%;
    }

    &.single-item {
        flex: 1 0 100%;

        .recommended-product {
            &__content {
                @include screen-md {
                    flex: 1 0 75%;
                    flex-direction: row;
                    justify-content: space-around;
                    align-items: center;
                }
            }

            &__image {
                @include screen-md {
                    flex: 0 0 20%;
                }
            }
        }
    }

    &__image {
        flex: 0 0 30%;

        @include screen-md {
            flex: 0 0 25%;
        }

        .product__image {
            margin-bottom: 0;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;

        @include screen-md {
            justify-content: space-between;
            padding-left: 20px;
        }

        .product__name {
            justify-content: flex-start;
            height: auto;
        }

        .product__price {
            justify-content: flex-start;

            a.image-container {
                height: 120px;
            }
        }
    }
}
</style>
